import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { accentPresets, appearanceModes, languageOptions, generateCSSVariables } from '../styles/designTokens';

/**
 * Theme Context - Phase 9.1
 * 
 * Provides unified theming across all FundElite portals.
 * - Accent/color preferences
 * - Appearance mode (light/dark/system)
 * - Language preference
 */

const STORAGE_KEY = 'fundelite_preferences';

const defaultPreferences = {
  accentPreset: 'fundelite_emerald',
  appearanceMode: 'system', // 'system' | 'light' | 'dark'
  language: 'en',
};

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [resolvedMode, setResolvedMode] = useState('dark');
  const [isLoaded, setIsLoaded] = useState(false);

  // Load preferences from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch (e) {
        console.error('Failed to parse preferences:', e);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, [preferences]);

  // Resolve appearance mode (system detection)
  useEffect(() => {
    const resolveMode = () => {
      if (preferences.appearanceMode === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        return systemDark ? 'dark' : 'light';
      }
      return preferences.appearanceMode;
    };

    setResolvedMode(resolveMode());

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (preferences.appearanceMode === 'system') {
        setResolvedMode(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preferences.appearanceMode]);

  // Apply CSS variables and class to document
  useEffect(() => {
    if (!isLoaded) return;

    const cssVars = generateCSSVariables(preferences.accentPreset, resolvedMode);
    const root = document.documentElement;

    // Apply CSS variables
    Object.entries(cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply dark/light class
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedMode);

    // Apply language attribute
    root.setAttribute('lang', preferences.language);
  }, [preferences, resolvedMode, isLoaded]);

  // Setters
  const setAccentPreset = useCallback((preset) => {
    if (accentPresets[preset]) {
      savePreferences({ accentPreset: preset });
    }
  }, [savePreferences]);

  const setAppearanceMode = useCallback((mode) => {
    if (appearanceModes[mode]) {
      savePreferences({ appearanceMode: mode });
    }
  }, [savePreferences]);

  const setLanguage = useCallback((lang) => {
    if (languageOptions[lang]) {
      savePreferences({ language: lang });
    }
  }, [savePreferences]);

  const resetToDefaults = useCallback(() => {
    setPreferences(defaultPreferences);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultPreferences));
  }, []);

  const value = {
    // Current preferences
    preferences,
    accentPreset: preferences.accentPreset,
    appearanceMode: preferences.appearanceMode,
    resolvedMode,
    language: preferences.language,
    
    // Setters
    setAccentPreset,
    setAppearanceMode,
    setLanguage,
    resetToDefaults,
    
    // Options
    accentPresets,
    appearanceModes,
    languageOptions,
    
    // State
    isLoaded,
    isDark: resolvedMode === 'dark',
    isLight: resolvedMode === 'light',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
